import { useState, useEffect } from "react";
import api from "@/lib/api";
import { formatTime } from "@/utils/formatters";
import { RiskBadge as StatusBadge } from "@/components/RiskBadge";
import { GuestTags } from "@/components/GuestTags";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSettings, useRiskLabels, useCustomRiskLevels } from "@/hooks/useValidations";
import { useWs } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import { Car } from "@phosphor-icons/react";

export function FlaggedTab() {
  const [flagged, setFlagged] = useState([]);
  const [total, setTotal] = useState(0);
  const [newIds, setNewIds] = useState(new Set());
  const settings = useSettings();
  const timezone = settings?.timezone || "UTC";
  const riskLabels = useRiskLabels(settings);
  const customLevels = useCustomRiskLevels(settings);
  const ws = useWs();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/validations/flagged?limit=100");
        if (!cancelled) {
          setFlagged(data.validations);
          setTotal(data.total);
        }
      } catch (err) {
        console.error("Failed to load flagged:", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Live flagged alerts via WebSocket
  useEffect(() => {
    if (!ws) return;
    return ws.on("flagged_alert", (alert) => {
      toast.error(`FLAGGED: ${alert.car_plate} in Room ${alert.room_number} — Score ${alert.risk_score}`, { duration: 8000 });
      // Reload flagged list
      (async () => {
        try {
          const { data } = await api.get("/validations/flagged?limit=100");
          setFlagged(data.validations);
          setTotal(data.total);
          setNewIds((prev) => new Set(prev).add(alert.car_plate));
          setTimeout(() => {
            setNewIds((prev) => {
              const next = new Set(prev);
              next.delete(alert.car_plate);
              return next;
            });
          }, 4000);
        } catch (err) { console.error("Failed to refresh flagged:", err); }
      })();
    });
  }, [ws]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-[#A3A3A3]">{total} flagged records</p>
      <div className="overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[#262626] hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Car Plate</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Room</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373] text-center">Validated Count</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Operator</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Time</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Reasons</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {flagged.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="text-center py-12 text-[#737373] text-sm">
                  No flagged cases
                </TableCell>
              </TableRow>
            ) : (
              flagged.map((v) => (
                <TableRow
                  key={v.id}
                  className={`border-b border-[#1A1A1A] hover:bg-[#1A1A1A] ${
                    v.over_limit ? "bg-[#FF3B30]/5 border-l-2 border-l-[#FF3B30]" : ""
                  } ${newIds.has(v.car_plate) ? "animate-ws-highlight" : ""}`}
                >
                  <TableCell className="font-mono-data text-sm text-white tracking-wider">
                    {v.car_plate}
                    {Array.from({ length: Math.min(v.plate_edit_count || 0, 2) }).map((_, i) => (
                      <Car key={`edit-${i}`} size={12} weight="fill" className="inline ml-0.5 text-[#FF9F0A]" />
                    ))}
                  </TableCell>
                  <TableCell className="font-mono-data text-sm text-[#A3A3A3]">{v.room_number}<GuestTags vip={v.guest_vip} rateCode={v.guest_rate_code} /></TableCell>
                  <TableCell className="font-mono-data text-sm text-[#007AFF] text-center">{v.room_validated_count ?? "-"}</TableCell>
                  <TableCell className="text-sm text-[#A3A3A3] cursor-default uppercase" title={v.user_name}>{v.user_username || v.user_name}</TableCell>
                  <TableCell className="font-mono-data text-xs text-[#737373]">{formatTime(v.timestamp, timezone)}</TableCell>
                  <TableCell><StatusBadge status={v.risk_status} score={v.risk_score} labels={riskLabels} customLevels={customLevels} /></TableCell>
                  <TableCell className="text-xs text-[#737373] max-w-[200px]">{v.risk_reasons?.join(", ")}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
